import {
    CdkOverlayOrigin,
    FlexibleConnectedPositionStrategyOrigin,
    OverlayModule,
} from '@angular/cdk/overlay';
import {
    Component,
    EventEmitter,
    Input,
    Output,
    signal,
    WritableSignal,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LanguageCode } from '../../models/language-code';
import { langCodeToIntlFEData } from '../../models/languages-fe-model';

@Component({
    selector: 'apcam-language-select',
    templateUrl: './language-select.component.html',
    styleUrls: ['./language-select.component.scss'],
    imports: [
        MatCardModule,
        MatChipsModule,
        MatDividerModule,
        OverlayModule,
        TranslatePipe,
    ],
})
export class LanguageSelectComponent {
    @Input()
    languages: WritableSignal<LanguageCode[]> = signal([]);

    /**
     * Controls whether the overlay is open - two-way data binding.
     *
     * @default false
     */
    @Input()
    isOpen: boolean = false;

    /**
     * Controls whether the overlay is open - two-way data binding.
     */
    @Output()
    isOpenChange: EventEmitter<boolean> = new EventEmitter();

    /**
     * The `trigger` input property is used to specify the trigger element or origin for the component overlay.
     * It is marked as required, and an error will be thrown if the property is not provided.
     *
     * @throws {Error} Will throw an error if the `trigger` property is not provided.
     */
    @Input({ required: true })
    trigger!: CdkOverlayOrigin | FlexibleConnectedPositionStrategyOrigin;

    /**
     * Emits language on language change event.
     */
    @Output()
    langChange: EventEmitter<LanguageCode> = new EventEmitter();

    constructor(private readonly translateService: TranslateService) {}

    /**
     * Changes a new language in the translation service and emits a language change event.
     *
     * @param newLang The language code to set as the new selected language.
     */
    protected changeLanguage(newLang: LanguageCode): void {
        this.translateService.use(newLang);
        this.langChange.emit(newLang);
    }

    /**
     * Checks if the provided language is currently selected in the translation service.
     *
     * @param lang The language code to check against the current selected language.
     * @returns True if the provided language is currently selected, false otherwise.
     */
    protected isLangSelected(lang: LanguageCode): boolean {
        return this.translateService.getCurrentLang() === lang;
    }

    /**
     * Maps a language code to a corresponding country code for icon representation.
     *
     * @param lang The language code to map to a country code.
     * @returns The corresponding country code for the given language.
     */
    protected mapLangToCountry(lang: LanguageCode): string {
        return langCodeToIntlFEData.get(lang)?.flagCode ?? '';
    }

    /**
     * Closes the overlay.
     */
    protected closeOverlay(): void {
        this.isOpenChange.emit(false);
    }
}
